import { getCropLifecycle } from '../utils/cropLifecycle';

function normalizePlanting(planting = {}) {
  const dataPlantio = planting.dataPlantio || planting.data_plantio || null;
  const dataColheita = planting.dataColheita || planting.data_colheita || null;
  const lifecycle = getCropLifecycle({
    cultura: planting.cultura,
    data_plantio: dataPlantio,
    data_colheita: dataColheita,
  });
  return {
    id: planting.id,
    cultura: planting.cultura || '',
    variedade: planting.variedade || '',
    data_plantio: dataPlantio,
    data_colheita_prevista: planting.dataColheitaPrevista || planting.data_colheita_prevista || null,
    data_colheita: dataColheita,
    status: planting.status === 'cancelado'
      ? 'cancelado'
      : lifecycle === 'planned' ? 'planejado' : (lifecycle === 'harvested' ? 'colhido' : (planting.status || 'incompleto')),
    area: planting.area ?? null,
    geometria: planting.geometria || null,
    observacoes: planting.observacoes || '',
    fonte_legada: planting.fonteLegada || planting.fonte_legada || null,
    fonte_legada_id: planting.fonteLegadaId ?? planting.fonte_legada_id ?? null,
    talhao_legado_id: planting.talhaoLegadoId ?? planting.talhao_legado_id ?? null,
    nome_legado: planting.nomeLegado || planting.nome_legado || '',
  };
}

function normalizeLegacyPlotLinks(plot = {}, plantings = []) {
  const declaredLinks = plot.talhoesLegados || plot.talhoes_legados || [];
  const links = declaredLinks
    .map(item => typeof item === 'object' ? item : { id: item })
    .filter(item => item?.id != null);
  const plantingLinks = plantings
    .filter(item => item.talhao_legado_id != null)
    .map(item => ({ id: item.talhao_legado_id, nome: item.nome_legado || '' }));
  const unique = [...new Map([...links, ...plantingLinks]
    .map(item => [String(item.id), { id: item.id, nome: item.nome || '' }])).values()];
  const requestedPrimary = plot.talhaoLegadoPrincipalId
    ?? plot.talhao_legado_principal_id
    ?? null;
  const currentPlanting = plantings.find(item => ['ativo', 'planejado'].includes(item.status));
  const primaryId = requestedPrimary
    ?? currentPlanting?.talhao_legado_id
    ?? unique[0]?.id
    ?? null;
  return { links: unique, primaryId };
}

export function operationalPlantingToLegacyPlot(physicalPlot = {}, planting = {}, property = {}) {
  // Plantios criados direto no talhão físico não têm vínculo próprio; usam o talhão legado principal.
  const legacyPlotId = planting.talhao_legado_id
    ?? (planting.fonte_legada === 'talhoes' ? planting.fonte_legada_id : null)
    ?? (!planting.fonte_legada ? physicalPlot.talhao_legado_principal_id : null);

  if (!legacyPlotId) return null;

  return {
    id: legacyPlotId,
    nome: planting.nome_legado || planting.cultura || physicalPlot.nome || 'Plantio histórico',
    propriedade_id: property.id ?? physicalPlot.propriedade_id ?? null,
    propriedade_nome: property.nome || physicalPlot.propriedade_nome || '',
    propriedade_municipio: property.municipio || physicalPlot.propriedade_municipio || '',
    propriedade_estado: property.estado || physicalPlot.propriedade_estado || '',
    cultura: planting.cultura || '',
    variedade: planting.variedade || '',
    data_plantio: planting.data_plantio || null,
    data_colheita_prevista: planting.data_colheita_prevista || null,
    data_colheita: planting.data_colheita || null,
    status: planting.status || 'incompleto',
    area: physicalPlot.area ?? planting.area ?? null,
    area_ha: physicalPlot.area ?? planting.area ?? null,
    geometria: planting.geometria || physicalPlot.geometria || null,
    coordenada_x: physicalPlot.coordenada_x ?? null,
    coordenada_y: physicalPlot.coordenada_y ?? null,
    observacoes: planting.observacoes || '',
    _operationalHistory: true,
    _physicalPlotId: physicalPlot.id ?? null,
    _plantingId: planting.id ?? null,
  };
}
export function normalizeOperationalPlot(plot = {}) {
  const rawPlantings = Array.isArray(plot.plantios) ? plot.plantios : [];
  const plantings = rawPlantings
    .map(normalizePlanting)
    .filter(item => item.status !== 'cancelado');
  const currentPlanting = plantings.find(item => item.status === 'ativo')
    || plantings.find(item => item.status === 'planejado')
    || null;
  const lastPlanting = plantings[0] || null;
  const legacy = normalizeLegacyPlotLinks(plot, plantings);

  return {
    id: plot.id,
    propriedade_id: plot.propriedadeId ?? plot.propriedade_id ?? null,
    nome: plot.nome || 'Talhão físico',
    area: plot.area ?? null,
    geometria: plot.geometria || null,
    coordenada_x: plot.coordenadaX ?? plot.coordenada_x ?? null,
    coordenada_y: plot.coordenadaY ?? plot.coordenada_y ?? null,
    observacoes: plot.observacoes || '',
    cultura: currentPlanting?.cultura || lastPlanting?.cultura || '',
    data_plantio: currentPlanting?.data_plantio || lastPlanting?.data_plantio || null,
    data_colheita: currentPlanting?.data_colheita || lastPlanting?.data_colheita || null,
    ciclo_atual: currentPlanting,
    ultimo_ciclo: lastPlanting,
    plantios: plantings,
    temHistorico: plot.temHistorico === true || rawPlantings.length > 0,
    talhoes_legados: legacy.links,
    talhao_legado_principal_id: legacy.primaryId,
    _legacyPlotIds: legacy.links.map(item => item.id),
    _operationalPhysical: true,
  };
}

export function operationalPhysicalPlotToCompatibleLegacyPlot(
  rawPhysicalPlot = {},
  legacyPlots = [],
  property = {}
) {
  const physicalPlot = normalizeOperationalPlot(rawPhysicalPlot);
  const primaryLegacyId = physicalPlot.talhao_legado_principal_id;
  const legacyPlot = legacyPlots.find(item => String(item.id) === String(primaryLegacyId));
  const compatibleId = primaryLegacyId ?? `physical:${physicalPlot.id}`;
  const currentPlanting = physicalPlot.ciclo_atual;
  const lastPlanting = physicalPlot.ultimo_ciclo;

  return {
    ...(legacyPlot || {}),
    id: compatibleId,
    propriedade_id: property.id ?? physicalPlot.propriedade_id ?? legacyPlot?.propriedade_id ?? null,
    propriedade_nome: property.nome || legacyPlot?.propriedade_nome || '',
    propriedade_municipio: property.municipio || legacyPlot?.propriedade_municipio || '',
    propriedade_estado: property.estado || legacyPlot?.propriedade_estado || '',
    nome: physicalPlot.nome,
    area: physicalPlot.area,
    area_ha: physicalPlot.area,
    geometria: physicalPlot.geometria,
    coordenada_x: physicalPlot.coordenada_x,
    coordenada_y: physicalPlot.coordenada_y,
    observacoes: physicalPlot.observacoes,
    cultura: currentPlanting?.cultura || lastPlanting?.cultura || '',
    variedade: currentPlanting?.variedade || lastPlanting?.variedade || '',
    data_plantio: currentPlanting?.data_plantio || lastPlanting?.data_plantio || null,
    data_colheita_prevista: currentPlanting?.data_colheita_prevista || null,
    data_colheita: currentPlanting?.data_colheita || lastPlanting?.data_colheita || null,
    status: currentPlanting?.status || lastPlanting?.status || 'incompleto',
    ciclo_atual: currentPlanting,
    ultimo_ciclo: lastPlanting,
    plantios: physicalPlot.plantios,
    temHistorico: physicalPlot.temHistorico,
    _physicalPlotId: physicalPlot.id,
    _legacyPlotIds: physicalPlot._legacyPlotIds,
    _legacyCompatibilityMissing: primaryLegacyId == null,
    _operationalPhysical: true,
    _canonicalAreaSource: 'talhao_fisico',
  };
}

export function applyCanonicalPhysicalPlots(
  legacyPlots = [],
  operationalStructure = null,
  propertyOrId = null
) {
  const propertyId = typeof propertyOrId === 'object' ? propertyOrId?.id : propertyOrId;
  const property = typeof propertyOrId === 'object' ? propertyOrId : { id: propertyId };
  const operationalProperty = operationalStructure?.propriedades?.find(item => (
    String(item.id) === String(propertyId)
  ));
  // Talhões físicos só existem para propriedades migradas e ativadas; cadastros
  // novos sempre gravam diretamente na tabela legada de talhões.
  const usesPhysicalPlots = operationalProperty?.origem === 'migracao_legado'
    && operationalProperty?.statusMigracao === 'ativado';
  if (!usesPhysicalPlots) return legacyPlots;

  return (operationalProperty.talhoes || []).map(plot => (
    operationalPhysicalPlotToCompatibleLegacyPlot(plot, legacyPlots, property)
  ));
}

export function plotMatchesLegacyId(plot = {}, candidateId = null) {
  if (candidateId == null) return false;
  return String(plot.id) === String(candidateId)
    || (plot._legacyPlotIds || []).some(id => String(id) === String(candidateId));
}

export function resolveOperationalPlotName(plots = [], candidateId = null) {
  const matches = plots.filter(plot => plotMatchesLegacyId(plot, candidateId));
  return matches.length === 1 ? matches[0].nome?.trim() || null : null;
}

export function applyCanonicalPhysicalAreas(legacyPlots = [], operationalStructure = null, propertyId = null) {
  const operationalProperty = operationalStructure?.propriedades?.find(item => (
    String(item.id) === String(propertyId)
  ));
  if (!operationalProperty?.talhoes?.length) return legacyPlots;

  const areaByLegacyPlot = new Map();
  operationalProperty.talhoes.forEach(rawPlot => {
    const physicalPlot = normalizeOperationalPlot(rawPlot);
    physicalPlot.plantios.forEach(planting => {
      const legacyId = planting.talhao_legado_id
        ?? (planting.fonte_legada === 'talhoes' ? planting.fonte_legada_id : null);
      if (legacyId != null && physicalPlot.area != null) {
        areaByLegacyPlot.set(String(legacyId), Number(physicalPlot.area));
      }
    });
  });

  return legacyPlots.map(plot => areaByLegacyPlot.has(String(plot.id))
    ? { ...plot, area: areaByLegacyPlot.get(String(plot.id)), _canonicalAreaSource: 'talhao_fisico' }
    : plot);
}

export function integrateOperationalProperties(properties = [], operationalStructure = null) {
  if (!operationalStructure || !Array.isArray(operationalStructure.propriedades)) {
    return properties;
  }

  const statusByProperty = new Map(
    operationalStructure.propriedades.map(item => [String(item.id), item])
  );

  return properties.reduce((visible, property) => {
    const operational = statusByProperty.get(String(property.id));
    if (operational?.statusOperacional === 'arquivado') return visible;

    // Talhões físicos só existem para propriedades migradas e ativadas; cadastros
    // novos sempre gravam diretamente na tabela legada de talhões.
    const usesPhysicalPlots = operational?.origem === 'migracao_legado'
      && operational?.statusMigracao === 'ativado';
    visible.push({
      ...property,
      _operationalOrigin: operational?.origem || 'cadastro_novo',
      _migrationStatus: operational?.statusMigracao || null,
      _operationalAvailable: operational?.visivel === true,
      _usesPhysicalPlots: usesPhysicalPlots,
      _operationalPlots: usesPhysicalPlots
        ? (operational.talhoes || []).map(normalizeOperationalPlot)
        : null,
    });
    return visible;
  }, []);
}
